import { useEffect, useMemo } from 'react';

import { useEffectiveFilters } from '../../next/CustomDash/filters/hooks/useEffectiveFilters';
import { useDataInspectorStore } from './data-inspector.store';
import { summariseFilterSelection } from './filter-selection-summary';

/**
 * Mirrors the active filter selection into the store so the recorder exchange
 * can stamp it onto each record.
 *
 * Reads `useEffectiveFilters` rather than the raw `activeFilterMap`, which is
 * the whole point: the map holds EVERY filter the page declares, nearly all of
 * them sitting at their definition defaults. Summarising it directly produced
 * thirteen chips — `Headcount 0–10000`, `Pay 0–3000000`, `Inflow 0–100` — around
 * the one selection that identified the view. Neither `isVisible` nor
 * `isUserSubmitted` distinguishes them: `isVisible: true` sits on the
 * *definition* (it means "may be chipped", not "was set"), and AI-applied
 * filters are deliberately NOT user-submitted.
 *
 * `useEffectiveFilters` is the app's own answer — it runs each filter's handler
 * `isActive(state)`, which is what the filter bar itself renders from. Reusing
 * it means user-set, AI-applied, segmented and multi filters are all classified
 * exactly as the bar classifies them, with no second rulebook to drift.
 *
 * Rendered from `CustomDashRoute` inside `PageProvider`, NOT from
 * `FilterProvider`: it needs page config as well as filter state, and importing
 * this from `FilterProvider` created a cycle that died under Jest with
 * "Cannot access 'FilterProvider' before initialization".
 */
export const DataInspectorFilterSync = () => {
  // `includeDisabledFilters` matches `useFilterTags`: a filter set on one page
  // and not supported by this one still shaped the user's intent, and the bar
  // keeps showing it (greyed) for exactly that reason.
  const effectiveFilters = useEffectiveFilters({
    includeDisabledFilters: true,
  });
  const setFilterSelection = useDataInspectorStore(
    (state) => state.setFilterSelection
  );

  const { signature, parts } = useMemo(
    () => summariseFilterSelection(effectiveFilters),
    [effectiveFilters]
  );

  // Fires on every filter recompute, which is more often than the selection
  // actually changes. `setFilterSelection` no-ops when nothing meaningful
  // moved, so the churn stops at the store rather than re-rendering the drawer.
  useEffect(() => {
    setFilterSelection(signature, parts);
  }, [signature, parts, setFilterSelection]);

  return null;
};
